import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Container,
  Link
} from '@mui/material';
import Header from './components/Header';
import Footer from './components/Footer';
import { useTranslation } from 'react-i18next';
import { BRAND_GRADIENTS } from '../../config/brand';

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: BRAND_GRADIENTS.vertical, // BRAND'den
  overflow: 'hidden', // Ana sayfanın kaydırmasını engelle
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto', // Sadece bu alanda kaydırma çubuğu olsun
  padding: theme.spacing(4, 2),
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 1),
    paddingBottom: theme.spacing(12),
  },
}));


const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  const getList = (key: string): string[] => {
    const list = t(key, { returnObjects: true });
    return Array.isArray(list) ? list : [];
  };

  const personalDataList = getList('privacyPolicy.dataCollection.types.personalData.list');
  const cookieExamplesList = getList('privacyPolicy.dataCollection.types.trackingCookies.list');
  const dataUsageList = getList('privacyPolicy.dataUsage.list');
  const legalRequirementsList = getList('privacyPolicy.dataDisclosure.legalRequirements.list');


  return (
    <PageContainer>
      <Header />

      <ContentSection>
        <Container 
          maxWidth="md" 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.95)', 
            color: 'black', 
            borderRadius: 2, 
            p: { xs: 3, md: 5 }
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('privacyPolicy.pageTitle')}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {t('privacyPolicy.lastUpdated')}
          </Typography>

          <Box mt={4}>
            <Typography variant="body1" paragraph>
              {t('privacyPolicy.introduction')}
          </Typography>

            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.definitions.title')}</Typography>
            <Typography variant="body1" paragraph><strong>{t('privacyPolicy.definitions.service').split(':')[0]}:</strong> {t('privacyPolicy.definitions.service').substring(t('privacyPolicy.definitions.service').indexOf(':') + 1)}</Typography>
            <Typography variant="body1" paragraph><strong>{t('privacyPolicy.definitions.personalData').split(':')[0]}:</strong> {t('privacyPolicy.definitions.personalData').substring(t('privacyPolicy.definitions.personalData').indexOf(':') + 1)}</Typography>
            <Typography variant="body1" paragraph><strong>{t('privacyPolicy.definitions.usageData').split(':')[0]}:</strong> {t('privacyPolicy.definitions.usageData').substring(t('privacyPolicy.definitions.usageData').indexOf(':') + 1)}</Typography>
            <Typography variant="body1" paragraph><strong>{t('privacyPolicy.definitions.cookies').split(':')[0]}:</strong> {t('privacyPolicy.definitions.cookies').substring(t('privacyPolicy.definitions.cookies').indexOf(':') + 1)}</Typography>
            
            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.dataCollection.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataCollection.description')}</Typography>

            <Typography variant="h6" gutterBottom mt={3}>{t('privacyPolicy.dataCollection.types.title')}</Typography>

            <Typography variant="subtitle1" gutterBottom mt={2}><strong>{t('privacyPolicy.dataCollection.types.personalData.title')}</strong></Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataCollection.types.personalData.p1')}</Typography>
            <ul>
              {personalDataList.map((item, index) => <li key={index}><Typography variant="body1">{item}</Typography></li>)}
            </ul>
            
            <Typography variant="subtitle1" gutterBottom mt={2}><strong>{t('privacyPolicy.dataCollection.types.usageData.title')}</strong></Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataCollection.types.usageData.p1')}</Typography>

            <Typography variant="subtitle1" gutterBottom mt={2}><strong>{t('privacyPolicy.dataCollection.types.locationData.title')}</strong></Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataCollection.types.locationData.p1')}</Typography>

            <Typography variant="subtitle1" gutterBottom mt={2}><strong>{t('privacyPolicy.dataCollection.types.trackingCookies.title')}</strong></Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataCollection.types.trackingCookies.p1')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataCollection.types.trackingCookies.p2')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataCollection.types.trackingCookies.examples')}</Typography>
            <ul>
              {cookieExamplesList.map((item, index) => <li key={index}><Typography variant="body1">{item}</Typography></li>)}
            </ul>
            
            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.dataUsage.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataUsage.description')}</Typography>
            <ul>
              {dataUsageList.map((item, index) => <li key={index}><Typography variant="body1">{item}</Typography></li>)}
            </ul>
            
            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.dataTransfer.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataTransfer.p1')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataTransfer.p2')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataTransfer.p3')}</Typography>

            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.dataDisclosure.title')}</Typography>
            <Typography variant="h6" gutterBottom mt={2}>{t('privacyPolicy.dataDisclosure.legalRequirements.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataDisclosure.legalRequirements.p1')}</Typography>
            <ul>
              {legalRequirementsList.map((item, index) => <li key={index}><Typography variant="body1">{item}</Typography></li>)}
            </ul>
            
            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.dataSecurity.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.dataSecurity.p1')}</Typography>

            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.serviceProviders.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.serviceProviders.p1')}</Typography>

            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.linksToOtherSites.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.linksToOtherSites.p1')}</Typography>

            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.childrensPrivacy.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.childrensPrivacy.p1')}</Typography>

            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.changesToPolicy.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.changesToPolicy.p1')}</Typography>
            
            <Typography variant="h5" gutterBottom mt={4}>{t('privacyPolicy.contactUs.title')}</Typography>
            <Typography variant="body1" paragraph>{t('privacyPolicy.contactUs.p1')}</Typography>
            <Typography variant="body1" paragraph>
              <Link href="mailto:info@mt-teknoloji.com" color="primary">
                {t('privacyPolicy.contactUs.email')}
              </Link>
          </Typography>
          </Box>
        </Container>
        <Footer />
      </ContentSection>
    </PageContainer>
  );
};

export default PrivacyPolicy; 